import React, { useState, useEffect } from 'react';
import { getCreditNotes, createCreditNote, applyCreditNote, getClients, getInvoices, getCreditNotePdf } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, CreditCard, Save, Download, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';

const statusColors = {
  'Active': 'bg-green-100 text-green-700 ring-1 ring-green-300',
  'Partially Used': 'bg-amber-100 text-amber-700 ring-1 ring-amber-300',
  'Used': 'bg-gray-100 text-gray-700 ring-1 ring-gray-300',
  'Open': 'bg-green-100 text-green-700 ring-1 ring-green-300'
};

export default function CreditNotesPage() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedCN, setSelectedCN] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientInvoices, setClientInvoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const [newCN, setNewCN] = useState({
    client_id: '',
    invoice_id: '',
    reason: '',
    amount: 0,
    notes: ''
  });

  const [applyData, setApplyData] = useState({
    invoice_id: '',
    amount: 0
  });

  useEffect(() => {
    loadCreditNotes();
    loadClients();
  }, []);

  useEffect(() => {
    if (newCN.client_id) {
      loadClientInvoices(newCN.client_id);
    }
  }, [newCN.client_id]);

  useEffect(() => {
    if (selectedCN?.client_id) {
      loadClientInvoices(selectedCN.client_id);
    }
  }, [selectedCN]);

  const loadCreditNotes = async () => {
    try {
      const res = await getCreditNotes({ limit: 100 });
      setCreditNotes(res.data.items);
      setTotal(res.data.total);
    } catch (error) {
      toast.error('Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await getClients({ limit: 1000 });
      setClients(res.data.items);
    } catch (error) {
      console.error(error);
    }
  };

  const loadClientInvoices = async (clientId) => {
    try {
      const res = await getInvoices({ client_id: clientId, limit: 100 });
      setClientInvoices(res.data.items.filter(inv => inv.balance_due > 0));
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateCN = async () => {
    if (!newCN.client_id || !newCN.reason || newCN.amount <= 0) {
      toast.error('Please fill required fields');
      return;
    }

    setSaving(true);
    try {
      await createCreditNote(newCN);
      toast.success('Credit note created');
      setDialogOpen(false);
      setNewCN({ client_id: '', invoice_id: '', reason: '', amount: 0, notes: '' });
      loadCreditNotes();
    } catch (error) {
      toast.error('Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyCN = async () => {
    if (!applyData.invoice_id || applyData.amount <= 0) {
      toast.error('Please select invoice and enter amount');
      return;
    }

    setSaving(true);
    try {
      await applyCreditNote(selectedCN.id, applyData.invoice_id, applyData.amount);
      toast.success('Credit applied to invoice');
      setApplyDialogOpen(false);
      setSelectedCN(null);
      setApplyData({ invoice_id: '', amount: 0 });
      loadCreditNotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply credit');
    } finally {
      setSaving(false);
    }
  };

  const openApplyDialog = (cn) => {
    setSelectedCN(cn);
    setApplyData({ invoice_id: '', amount: cn.balance_remaining });
    setApplyDialogOpen(true);
  };

  const handleDownloadPdf = async (id, creditNumber) => {
    setDownloading(id);
    try {
      const res = await getCreditNotePdf(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${creditNumber || 'credit-note'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(null);
    }
  };

  const getStatus = (cn) => {
    const balance = cn.balance_remaining ?? cn.amount;
    if (balance === 0) return 'Used';
    if (balance < cn.amount) return 'Partially Used';
    return 'Open';
  };

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Credit Notes</h1>
          <p className="text-muted-foreground">Issue and manage credit notes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-credit-note-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Credit Note
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Issue Credit Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label>Client *</Label>
                <Select value={newCN.client_id} onValueChange={(v) => setNewCN(p => ({ ...p, client_id: v }))}>
                  <SelectTrigger data-testid="cn-client">
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.display_name || c.trade_name || c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {newCN.client_id && clientInvoices.length > 0 && (
                <div>
                  <Label>Related Invoice (Optional)</Label>
                  <Select value={newCN.invoice_id} onValueChange={(v) => setNewCN(p => ({ ...p, invoice_id: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select invoice" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientInvoices.map((inv) => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.invoice_number} - {formatCurrency(inv.balance_due)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label>Reason *</Label>
                <Input
                  value={newCN.reason}
                  onChange={(e) => setNewCN(p => ({ ...p, reason: e.target.value }))}
                  placeholder="e.g., Overcharge correction, Service cancellation"
                  data-testid="cn-reason"
                />
              </div>

              <div>
                <Label>Amount (AED) *</Label>
                <Input
                  type="number"
                  value={newCN.amount}
                  onChange={(e) => setNewCN(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                  data-testid="cn-amount"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={newCN.notes}
                  onChange={(e) => setNewCN(p => ({ ...p, notes: e.target.value }))}
                  rows={2}
                />
              </div>

              <Button className="w-full" onClick={handleCreateCN} disabled={saving} data-testid="save-cn-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Creating...' : 'Create Credit Note'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : creditNotes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p>No credit notes yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Credit Note #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditNotes.map((cn) => {
                  const status = getStatus(cn);
                  return (
                    <TableRow key={cn.id} data-testid={`cn-row-${cn.id}`}>
                      <TableCell className="font-mono text-sm">{cn.credit_note_number || cn.credit_number}</TableCell>
                      <TableCell>{formatDate(cn.issue_date || cn.credit_date || cn.created_at)}</TableCell>
                      <TableCell className="font-medium">{cn.client_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{cn.reason}</TableCell>
                      <TableCell className="text-right font-mono">{formatCurrency(cn.amount)}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-600">
                        {formatCurrency(cn.balance_remaining ?? cn.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[status] || 'bg-gray-100 text-gray-700'}>
                          {status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(cn.balance_remaining ?? cn.amount) > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => openApplyDialog(cn)}
                              className="text-primary"
                              data-testid={`apply-cn-${cn.id}`}
                            >
                              <ArrowRight className="w-4 h-4 mr-1" />
                              Apply
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadPdf(cn.id, cn.credit_note_number || cn.credit_number)}
                            disabled={downloading === cn.id}
                            data-testid={`download-cn-pdf-${cn.id}`}
                          >
                            <Download className={`w-4 h-4 ${downloading === cn.id ? 'animate-pulse' : ''}`} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {creditNotes.length} of {total} credit notes
          </div>
        </CardContent>
      </Card>

      {/* Apply Credit Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Apply Credit to Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-sm text-muted-foreground">Credit Note: {selectedCN?.credit_note_number || selectedCN?.credit_number}</p>
              <p className="text-lg font-semibold text-emerald-700">{formatCurrency(selectedCN?.balance_remaining ?? selectedCN?.amount)} available</p>
            </div>

            <div>
              <Label>Select Invoice *</Label>
              <Select value={applyData.invoice_id} onValueChange={(v) => setApplyData(p => ({ ...p, invoice_id: v }))}>
                <SelectTrigger data-testid="apply-invoice-select">
                  <SelectValue placeholder="Select invoice" />
                </SelectTrigger>
                <SelectContent>
                  {clientInvoices.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Amount to Apply *</Label>
              <Input
                type="number"
                value={applyData.amount}
                onChange={(e) => setApplyData(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))}
                max={selectedCN?.balance_remaining ?? selectedCN?.amount}
                data-testid="apply-amount"
              />
            </div>

            <Button className="w-full" onClick={handleApplyCN} disabled={saving} data-testid="confirm-apply-btn">
              {saving ? 'Applying...' : 'Apply Credit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
